import { useState } from "react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import { Textarea } from "@/components/ui/textarea"
import {
  MktDialog,
  MktDialogContent,
  MktDialogDescription,
  MktDialogFooter,
  MktDialogFormFooter,
  MktDialogHeader,
  MktDialogTitle,
} from "@/components/mkt-dialog"
import { FileText, Sparkles, Upload, X } from "lucide-react"
import { toast } from "sonner"
import { RequireApproved } from "@/components/require-approved"
import { usePendingAction } from "@/hooks/use-pending-action"
import { JOB_DOCUMENT_ACCEPT, useJobAiDraft } from "@/features/jobs"
import {
  JobFormFields,
  useJobForm,
  type JobAiDraft,
} from "@/components/recruit/job-form"
import { capturePostHogEvent } from "@/lib/posthog"

export const Route = createFileRoute("/recruit/jobs_/new")({
  component: NewJobPage,
})

function NewJobPage() {
  return (
    <RequireApproved capability="manageJobLifecycle">
      <NewJobForm />
    </RequireApproved>
  )
}

function NewJobForm() {
  const create = useMutation(api.jobs.create)
  const navigate = useNavigate()
  const form = useJobForm()
  const [aiOpen, setAiOpen] = useState(false)
  const { pending: busy, run: runCreate } = usePendingAction()

  // Photo and LinkedIn must be set before publishing (the server enforces the same rule).
  const me = useQuery(api.profiles.me)
  const missingProfileItems = me
    ? [
        ...(!me.avatarStorageId ? ["a profile photo"] : []),
        ...(!me.linkedinUrl?.trim() ? ["your LinkedIn profile"] : []),
      ]
    : []
  const profileIncomplete = missingProfileItems.length > 0
  const [profileDialogDismissed, setProfileDialogDismissed] = useState(false)

  return (
    <div className="mkt-job-create">
      <header className="mkt-job-create__header">
        <div>
          <Link to="/recruit/jobs" className="mkt-event-create__back">
            ← Your jobs
          </Link>
          <h1 className="mkt-page-title">New job posting</h1>
          <p className="mkt-page-sub">
            Posting is free — publish when you&apos;re ready and start receiving
            applicants from the community.
          </p>
        </div>
        <button
          type="button"
          className="home-wf-btn home-wf-btn--outline inline-flex shrink-0 items-center gap-2"
          onClick={() => setAiOpen(true)}
        >
          <Sparkles className="size-4" aria-hidden />
          Draft with AI
        </button>
      </header>

      <AiDraftDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        currentTitle={form.title}
        onDraft={form.applyAiDraft}
      />

      <CompleteProfileDialog
        open={profileIncomplete && !profileDialogDismissed}
        onOpenChange={(open) => {
          if (!open) setProfileDialogDismissed(true)
        }}
        missingItems={missingProfileItems}
      />

      {profileIncomplete && (
        <div className="mkt-panel mb-6">
          <p className="mkt-panel__title">Complete your profile to post</p>
          <p className="mkt-panel__copy">
            Add {missingProfileItems.join(" and ")} before publishing.
          </p>
          <Link
            to="/recruit/settings/account"
            className="home-wf-btn home-wf-btn--lime mt-3 inline-flex"
          >
            Complete profile
          </Link>
        </div>
      )}

      <form
        className="mkt-job-create__form"
        onSubmit={(e) => {
          e.preventDefault()
          void runCreate(async () => {
            const id = await create(form.toArgs())
            capturePostHogEvent("job_posted", {
              employment_type: form.employmentType,
              work_mode: form.workMode,
            })
            toast.success("Job posted")
            void navigate({ to: "/recruit/jobs/$jobId", params: { jobId: id } })
          })
        }}
      >
        <JobFormFields
          form={form}
          titleVariant="hero"
          descriptionRows={8}
          idPrefix="new-job"
        />

        <div className="mkt-event-create__footer">
          <p className="mkt-event-create__footer-copy">
            Free forever · your listing goes live immediately after publish
          </p>
          <button
            type="submit"
            disabled={busy || profileIncomplete}
            className="home-wf-btn home-wf-btn--lime mkt-event-create__submit"
          >
            {busy ? "Publishing…" : "Publish job"}
          </button>
        </div>
      </form>
    </div>
  )
}

function CompleteProfileDialog({
  open,
  onOpenChange,
  missingItems,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  missingItems: string[]
}) {
  return (
    <MktDialog open={open} onOpenChange={onOpenChange}>
      <MktDialogContent size="md">
        <MktDialogHeader>
          <MktDialogTitle>Complete your profile to post</MktDialogTitle>
          <MktDialogDescription>
            Add {missingItems.join(" and ")} in account settings to publish
            this job.
          </MktDialogDescription>
        </MktDialogHeader>
        <MktDialogFooter>
          <button
            type="button"
            className="home-wf-btn home-wf-btn--outline"
            onClick={() => onOpenChange(false)}
          >
            Not now
          </button>
          <Link
            to="/recruit/settings/account"
            className="home-wf-btn home-wf-btn--lime"
          >
            Complete profile
          </Link>
        </MktDialogFooter>
      </MktDialogContent>
    </MktDialog>
  )
}

function AiDraftDialog({
  open,
  onOpenChange,
  currentTitle,
  onDraft,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTitle: string
  onDraft: (draft: JobAiDraft) => void
}) {
  const {
    document: doc,
    clearDocument,
    fileInputRef,
    uploading,
    uploadDocument,
    draft: runAiDraft,
  } = useJobAiDraft()
  const [notes, setNotes] = useState("")
  const { pending: busy, run: runDraft } = usePendingAction()

  const reset = () => {
    setNotes("")
    clearDocument()
  }

  // A document alone is enough; notes alone still work; both is best.
  const canSubmit = Boolean(doc) || notes.trim().length > 0

  return (
    <MktDialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <MktDialogContent size="md">
        <MktDialogHeader>
          <MktDialogTitle className="flex items-center gap-2">
            <Sparkles className="size-4" aria-hidden /> Draft with AI
          </MktDialogTitle>
          <MktDialogDescription>
            Upload an existing job description, or jot down rough notes — AI
            drafts a full job post either way. You review and edit before
            publishing.
          </MktDialogDescription>
        </MktDialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            if (!canSubmit) return
            void runDraft(
              async () => {
                const draft = await runAiDraft({
                  notes,
                  title: currentTitle || undefined,
                })
                onDraft(draft)
                toast.success("Draft ready — review and edit below")
                onOpenChange(false)
                reset()
              },
              { error: "Failed to draft" },
            )
          }}
        >
          <div className="mb-4 space-y-2">
            {doc ? (
              <div className="mkt-panel flex items-center gap-3 !py-3">
                <FileText className="size-4 shrink-0 opacity-70" aria-hidden />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {doc.fileName}
                </span>
                <button
                  type="button"
                  className="mkt-row-action shrink-0"
                  onClick={clearDocument}
                  disabled={busy}
                  aria-label={`Remove ${doc.fileName}`}
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="home-wf-btn home-wf-btn--outline inline-flex w-full items-center justify-center gap-2"
                disabled={uploading || busy}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="size-4" aria-hidden />
                {uploading
                  ? "Uploading…"
                  : "Upload a job description (PDF, DOCX or TXT)"}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={JOB_DOCUMENT_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void uploadDocument(file)
              }}
            />
          </div>

          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={
              doc
                ? "Optional — anything to change or add, e.g. \u201cdrop the relocation bit, comp is ₹18–25 LPA\u201d"
                : "e.g. senior backend engineer, own our payments service, need strong Node + Postgres, remote-friendly, 3-5 days/week in HSR office, ₹18–25 LPA"
            }
            rows={doc ? 3 : 6}
            autoFocus={!doc}
          />
          <MktDialogFormFooter
            busy={busy}
            disabled={!canSubmit || uploading}
            onCancel={() => onOpenChange(false)}
            saveLabel="Generate draft"
            busyLabel="Drafting…"
          />
        </form>
      </MktDialogContent>
    </MktDialog>
  )
}
